/**
 * Hook that returns a stable callback for toggling parental controls
 * from the TopNavbar lock icon. Handles password setup and verification.
 */
import { useCallback, useState } from 'react';
import { useParentalControls } from '../contexts/ParentalControlsContext';

interface UseLockToggleReturn {
  handleLockIconClick: () => void;
  /** Call this from your page's PasswordModal confirm handler. */
  handleLockPasswordConfirm: (raw: string) => void;
  handleLockPasswordCancel: () => void;
  lockModalOpen: boolean;
  lockModalMode: 'set' | 'verify';
  lockModalError: string;
}

/**
 * Returns handlers for the TopNavbar lock icon.
 *
 * Usage in a page:
 * ```tsx
 * const { handleLockIconClick, lockModalOpen, lockModalMode, lockModalError,
 *          handleLockPasswordConfirm, handleLockPasswordCancel } = useLockToggle();
 * ...
 * if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
 * ...
 * {lockModalOpen && (
 *   <PasswordModal mode={lockModalMode}
 *     error={lockModalError}
 *     onConfirm={handleLockPasswordConfirm}
 *     onCancel={handleLockPasswordCancel}
 *   />
 * )}
 * ```
 */
export function useLockToggle(): UseLockToggleReturn {
  const { isEnabled, hasPassword, verifyPassword, setPassword, setEnabled } = useParentalControls();
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [lockModalMode, setLockModalMode] = useState<'set' | 'verify'>('verify');
  const [lockModalError, setLockModalError] = useState('');

  const handleLockIconClick = useCallback(() => {
    if (!isEnabled) {
      if (!hasPassword()) {
        setLockModalError('');
        setLockModalMode('set');
        setLockModalOpen(true);
        return;
      }
      setEnabled(true);
      return;
    }
    // Disabling parental controls
    if (!hasPassword()) {
      setEnabled(false);
      return;
    }
    // Need password to disable
    setLockModalError('');
    setLockModalMode('verify');
    setLockModalOpen(true);
  }, [isEnabled, hasPassword, setEnabled]);

  const handleLockPasswordConfirm = useCallback(
    (raw: string) => {
      if (lockModalMode === 'set') {
        setPassword(raw);
        setEnabled(true);
        setLockModalOpen(false);
        return;
      }
      if (!verifyPassword(raw)) {
        setLockModalError('parental.wrongPassword'); // caller uses t() with this key
        return;
      }
      setLockModalOpen(false);
      setEnabled(false);
    },
    [lockModalMode, setPassword, verifyPassword, setEnabled]
  );

  const handleLockPasswordCancel = useCallback(() => {
    setLockModalOpen(false);
    setLockModalError('');
  }, []);

  return {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  };
}
